import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Key, Copy, X, AlertCircle, ShieldCheck, Settings2, CheckCircle2 } from 'lucide-react';
import DashboardShell from '@/components/layout/DashboardShell';
import api from '@/lib/api';
import { useAuth, formatApiError } from '@/lib/auth';
import { MODULE_TOOLS } from '@/pages/ModuleDashboard';
import { moduleBySlug } from '@/lib/modules';

export default function OrgUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [accessUser, setAccessUser] = useState(null);

  const load = async () => {
    try { const { data } = await api.get('/users'); setUsers(data.items || []); }
    catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const reset = async (uid) => {
    if (!window.confirm('Reset this user\'s password? They will be forced to change it on next login.')) return;
    try {
      const { data } = await api.post(`/auth/reset-password/${uid}`);
      setResetResult(data);
    } catch (e) { alert(formatApiError(e)); }
  };

  const remove = async (uid) => {
    if (!window.confirm('Delete this user?')) return;
    try { await api.delete(`/users/${uid}`); load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  const canManage = ['super_admin', 'org_manager'].includes(user?.role);

  return (
    <DashboardShell sectionTitle="Team & Access">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight text-navy">Team</h1>
          <p className="text-neutral-600 mt-1 text-sm">Manage your organization's users and reset passwords.</p>
        </div>
        {canManage && (
          <button onClick={() => setShowCreate(true)} className="btn-accent text-sm" data-testid="add-user-btn">
            <Plus className="w-4 h-4" /> New user
          </button>
        )}
      </div>

      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-50/50">
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-5 py-3">Name</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">Email</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">Role</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">Last login</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-neutral-100 hover:bg-neutral-50/60 transition-colors">
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-md bg-navy text-white flex items-center justify-center font-semibold text-xs">{(u.name || '').split(' ').map(w => w[0]).slice(0, 2).join('')}</div>
                    <div className="font-medium text-navy text-sm">{u.name}</div>
                    {u.must_change_password && <span className="text-[10px] font-mono text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">MUST RESET</span>}
                  </div>
                </td>
                <td className="px-3 py-3.5 text-sm text-neutral-700">{u.email}</td>
                <td className="px-3 py-3.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-neutral-100 text-neutral-700">{u.role}</span>
                </td>
                <td className="px-3 py-3.5 text-xs text-neutral-500 font-mono">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : '—'}</td>
                <td className="px-3 py-3.5">
                  {canManage && u.id !== user?.id && (
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setAccessUser(u)} className="px-2 py-1 text-[11px] font-medium rounded hover:bg-navy/10 text-navy inline-flex items-center gap-1" data-testid={`access-${u.id}`}>
                        <Settings2 className="w-3 h-3" /> Access
                      </button>
                      <button onClick={() => reset(u.id)} className="px-2 py-1 text-[11px] font-medium rounded hover:bg-neutral-100 text-amber-700 inline-flex items-center gap-1" data-testid={`reset-pwd-${u.id}`}>
                        <Key className="w-3 h-3" /> Reset password
                      </button>
                      <button onClick={() => remove(u.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (<tr><td colSpan={5} className="text-center py-12 text-neutral-500 text-sm">No users yet.</td></tr>)}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={load} canChooseOrg={user?.role === 'super_admin'} />}
      {resetResult && <ResetResultModal result={resetResult} onClose={() => setResetResult(null)} />}
      {accessUser && <AccessModal target={accessUser} onClose={() => setAccessUser(null)} onSaved={() => { setAccessUser(null); load(); }} />}
    </DashboardShell>
  );
}

function AccessModal({ target, onClose, onSaved }) {
  // Per-module allowed_tools editor. undefined = inherit all.
  const [allowed, setAllowed] = useState(() => ({ ...(target.allowed_tools || {}) }));
  const [orgModules, setOrgModules] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      if (!target.org_id) return;
      try {
        const { data } = await api.get(`/orgs/${target.org_id}`);
        setOrgModules(data.modules || []);
      } catch (e) { console.error(e); }
    })();
  }, [target.org_id]);

  const isRestricted = (modSlug) => Array.isArray(allowed[modSlug]);
  const isToolEnabled = (modSlug, toolKey) => {
    if (!isRestricted(modSlug)) return true;  // inherit all
    return allowed[modSlug].includes(toolKey);
  };
  const toggleTool = (modSlug, toolKey) => {
    const tools = MODULE_TOOLS[modSlug] || [];
    setAllowed((prev) => {
      const next = { ...prev };
      const cur = Array.isArray(next[modSlug]) ? [...next[modSlug]] : tools.map((t) => t.key);
      const idx = cur.indexOf(toolKey);
      if (idx >= 0) cur.splice(idx, 1); else cur.push(toolKey);
      next[modSlug] = cur;
      return next;
    });
  };
  const resetModule = (modSlug) => {
    setAllowed((prev) => {
      const next = { ...prev };
      delete next[modSlug];
      return next;
    });
  };
  const blockModule = (modSlug) => {
    setAllowed((prev) => ({ ...prev, [modSlug]: [] }));
  };

  const save = async () => {
    setBusy(true); setError('');
    try {
      await api.patch(`/users/${target.id}/access`, { allowed_tools: allowed });
      onSaved();
    } catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md w-full max-w-3xl max-h-[88vh] overflow-y-auto p-6">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h3 className="font-display font-semibold text-xl text-navy">Manage access — {target.name}</h3>
            <p className="text-sm text-neutral-500 mt-0.5">{target.email} · <span className="font-mono">{target.role}</span></p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-neutral-100"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-neutral-500 mt-3 mb-4 leading-relaxed">
          Choose which tools this user can access within each subscribed module. <strong>Inherit</strong> = user gets every tool the organisation subscribes to. <strong>Block all</strong> = revoke access to that module. Customise individual tools by toggling them.
        </p>

        <div className="space-y-4">
          {orgModules.map((modSlug) => {
            const mod = moduleBySlug(modSlug);
            if (!mod) return null;
            const Icon = mod.icon;
            const tools = MODULE_TOOLS[modSlug] || [];
            const restricted = isRestricted(modSlug);
            const totalAllowed = restricted ? allowed[modSlug].length : tools.length;
            return (
              <div key={modSlug} className="border border-neutral-200 rounded-md" data-testid={`access-mod-${modSlug}`}>
                <div className="px-4 py-3 bg-neutral-50/60 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="h-7 w-7 rounded flex items-center justify-center" style={{ background: `${mod.accent}18`, color: mod.accent }}>
                      <Icon className="w-4 h-4" strokeWidth={1.8} />
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-navy">{mod.name}</div>
                      <div className="text-[11px] text-neutral-500">{totalAllowed}/{tools.length} tools enabled · {restricted ? 'customised' : 'inherits all'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => resetModule(modSlug)} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded border border-neutral-200 hover:bg-neutral-100">Inherit</button>
                    <button onClick={() => blockModule(modSlug)} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50">Block all</button>
                  </div>
                </div>
                <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                  {tools.length === 0 && <div className="col-span-3 text-xs text-neutral-400 py-2">No tools registered for this module.</div>}
                  {tools.map((t) => {
                    const enabled = isToolEnabled(modSlug, t.key);
                    return (
                      <button key={t.key} type="button" onClick={() => toggleTool(modSlug, t.key)}
                        className={`text-left px-2.5 py-1.5 rounded border text-xs transition-all ${enabled ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-neutral-200 bg-white text-neutral-400 hover:border-neutral-300'}`}
                        data-testid={`tool-toggle-${modSlug}-${t.key}`}>
                        <div className="flex items-center gap-1.5">
                          {enabled ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <span className="w-3 h-3 inline-block rounded-full border border-neutral-300" />}
                          <span className="truncate font-medium">{t.label}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {orgModules.length === 0 && <div className="text-sm text-neutral-500 py-4">Organisation has no active modules.</div>}
        </div>

        {error && <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-5 mt-4 border-t">
          <button type="button" onClick={onClose} className="btn-outline text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-accent text-sm" data-testid="save-access">{busy ? 'Saving…' : 'Save access'}</button>
        </div>
      </div>
    </div>
  );
}

function CreateUserModal({ onClose, onCreated, canChooseOrg }) {
  const [form, setForm] = useState({ name: '', email: '', username: '', phone: '', password: '', role: 'team_member', org_id: '' });
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (canChooseOrg) return;
    (async () => {
      try { const { data } = await api.get('/users/inheritance-preview'); setPreview(data); }
      catch (e) { /* ignore */ }
    })();
  }, [canChooseOrg]);
  const submit = async (e) => {
    e.preventDefault(); setError(''); setBusy(true);
    try {
      const payload = { ...form };
      if (!canChooseOrg) delete payload.org_id;
      if (!payload.username) delete payload.username;
      if (!payload.phone) delete payload.phone;
      await api.post('/users', payload);
      onCreated(); onClose();
    } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md w-full max-w-lg p-6">
        <h3 className="font-display font-semibold text-xl text-navy mb-1">New user</h3>
        <p className="text-xs text-neutral-500 mb-4">The new user automatically inherits the organisation context shown below — no need to re-enter.</p>

        {preview && !preview.super_admin && (
          <div className="mb-4 p-3 bg-navy/5 border border-navy/15 rounded-md" data-testid="inheritance-preview">
            <div className="text-[10px] uppercase tracking-wider text-navy/70 font-semibold mb-1.5">Auto-inherits</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-neutral-500">Organisation</span><div className="font-medium text-navy">{preview.org_name}</div></div>
              <div><span className="text-neutral-500">Org ID</span><div className="font-mono text-navy">{preview.org_no}</div></div>
              <div className="col-span-2"><span className="text-neutral-500">Modules</span>
                <div className="flex flex-wrap gap-1 mt-1">{preview.modules.map((m) => <span key={m} className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-navy/10 text-navy">{m}</span>)}</div>
              </div>
              <div className="col-span-2 text-[10px] text-neutral-500 mt-1">Branding, templates and workflow rules are also auto-applied from the org config center.</div>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <Inp label="Full name" v={form.name} on={(v) => setForm({ ...form, name: v })} req />
          <Inp label="Email" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} req />
          <div className="grid grid-cols-2 gap-3">
            <Inp label="Username (optional)" v={form.username} on={(v) => setForm({ ...form, username: v })} />
            <Inp label="Phone (optional)" v={form.phone} on={(v) => setForm({ ...form, phone: v })} />
          </div>
          <Inp label="Initial password" type="password" v={form.password} on={(v) => setForm({ ...form, password: v })} req min={8} />
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1.5">Role</span>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none bg-white">
              <option value="team_member">Team Member · self-service only</option>
              <option value="hr_officer">HR Officer · HR functions per permission</option>
              <option value="department_manager">Department Manager · supervise dept</option>
              <option value="security_officer">Security Officer · visitor + incidents</option>
              <option value="staff">Staff (legacy)</option>
              <option value="client">Client / End-user</option>
              {canChooseOrg && <option value="org_manager">Org Manager · full org access</option>}
            </select>
          </label>
          {canChooseOrg && <Inp label="Organisation ID (slug)" v={form.org_id} on={(v) => setForm({ ...form, org_id: v })} req />}
          {error && <div className="p-3 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-700">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline text-sm">Cancel</button>
            <button type="submit" disabled={busy} className="btn-accent text-sm" data-testid="submit-create-user">{busy ? 'Creating…' : 'Create user'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetResultModal({ result, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md w-full max-w-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-emerald-600" />
          <h3 className="font-display font-semibold text-lg text-navy">Password reset</h3>
        </div>
        <p className="text-sm text-neutral-600 mb-4">Share this temporary password with <strong>{result.user_email}</strong>. They'll be required to set a new one on next sign-in.</p>
        <div className="flex items-center gap-2 p-3 bg-neutral-50 border border-neutral-200 rounded font-mono text-sm">
          <span className="flex-1 break-all">{result.temp_password}</span>
          <button onClick={() => { navigator.clipboard.writeText(result.temp_password); }} className="p-1.5 rounded hover:bg-neutral-200" title="Copy">
            <Copy className="w-4 h-4" />
          </button>
        </div>
        <button onClick={onClose} className="btn-primary w-full mt-4 justify-center" data-testid="close-reset-result">Done</button>
      </div>
    </div>
  );
}

function Inp({ label, v, on, type = 'text', req, min }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1.5">{label}</span>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} required={req} minLength={min} className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none focus:border-navy" />
    </label>
  );
}
